import React from "react";
import {
  LayoutDashboard, Heart, FileText, Receipt, TrendingUp,
  Calendar, MessageCircle, HandHeart, UserCog,
  BarChart3, X, Menu, ChevronRight, LogOut,
} from "lucide-react";

const navSections = [
  {
    section: "Overview",
    items: [
      { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    ],
  },
  {
    section: "Collections",
    items: [
      { id: "donations", label: "Donations", icon: Heart },
      { id: "receipts", label: "Receipts", icon: Receipt },
    ],
  },
  {
    section: "Finance",
    items: [
      { id: "expenses", label: "Expenses", icon: TrendingUp },
      { id: "finance", label: "Finance", icon: BarChart3 },
    ],
  },
  {
    section: "Events",
    items: [
      { id: "events", label: "Events", icon: Calendar },
    ],
  },
  {
    section: "Engagement",
    items: [
      { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
      { id: "sponsors", label: "Sponsors", icon: HandHeart },
    ],
  },
  {
    section: "Admin",
    items: [
      { id: "members", label: "Members", icon: UserCog },
      { id: "reports", label: "Reports", icon: FileText },
    ],
  },
];

const MEMBER_PAGES = ["donations", "receipts"];

export default function Sidebar({ activePage, setActivePage, mobileOpen, setMobileOpen, onSignOut, userRole }) {
  const handleNav = (id) => {
    setActivePage(id);
    setMobileOpen(false);
  };

  const visibleSections =
    userRole === "member"
      ? navSections
          .map((s) => ({ ...s, items: s.items.filter((i) => MEMBER_PAGES.includes(i.id)) }))
          .filter((s) => s.items.length > 0)
      : navSections;

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-gray-900 text-white">
      {/* Logo */}
      <div className="px-5 pt-6 pb-4 border-b border-gray-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-orange-500 rounded-xl flex items-center justify-center text-xl shadow-lg">
              🕉️
            </div>
            <div>
              <p className="text-sm font-bold text-white leading-tight" style={{ fontFamily: "Poppins, sans-serif" }}>
                Utsav by Btrix
              </p>
              <p className="text-xs text-orange-400 font-semibold">Manager</p>
            </div>
          </div>
          <button
            className="lg:hidden text-gray-400 hover:text-white"
            onClick={() => setMobileOpen(false)}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Committee Badge */}
      <div className="mx-4 mt-4 mb-2 bg-gradient-to-r from-orange-500/20 to-amber-500/10 border border-orange-500/30 rounded-xl p-3">
        <p className="text-[11px] text-orange-300 font-medium leading-tight">
          Sri Siddhi Vinayaka Utsava Samithi
        </p>
        <div className="flex items-center gap-1 mt-1">
          <span className="text-yellow-400 text-xs">⭐</span>
          <span className="text-xs text-gray-300 font-medium">Professional Plan</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {visibleSections.map(({ section, items }) => (
          <div key={section} className="mb-1">
            <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-widest px-3 py-1.5">
              {section}
            </p>
            {items.map(({ id, label, icon: Icon, badge }) => {
              const isActive = activePage === id;
              return (
                <button
                  key={id}
                  onClick={() => handleNav(id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group relative ${
                    isActive
                      ? "bg-orange-500 text-white shadow-lg shadow-orange-500/20"
                      : "text-gray-400 hover:text-white hover:bg-gray-800"
                  }`}
                >
                  <Icon size={16} className={isActive ? "text-white" : "text-gray-500 group-hover:text-orange-400"} />
                  <span className="flex-1 text-left">{label}</span>
                  {badge && (
                    <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                      {badge}
                    </span>
                  )}
                  {isActive && <ChevronRight size={12} className="text-orange-200" />}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-4 py-4 border-t border-gray-700/50">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">A</div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-white truncate">{userRole === "member" ? "Member" : "Admin User"}</p>
            <p className="text-[10px] text-gray-500 truncate">utsav.btrix.in</p>
          </div>
          <button onClick={onSignOut} title="Sign out"
            className="text-gray-500 hover:text-red-400 cursor-pointer flex-shrink-0 transition-colors">
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="sidebar-overlay lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-60 h-screen fixed left-0 top-0 z-30 shadow-xl">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      <aside
        className={`lg:hidden flex flex-col w-64 h-screen fixed left-0 top-0 z-50 shadow-2xl transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <SidebarContent />
      </aside>
    </>
  );
}

export function MobileHeader({ activePage, setMobileOpen }) {
  const allItems = navSections.flatMap((s) => s.items);
  const current = allItems.find((i) => i.id === activePage);
  const Icon = current?.icon;

  return (
    <header
      className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-200 px-4 flex items-center gap-3 shadow-sm"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))', paddingBottom: '0.75rem' }}
    >
      <button
        onClick={() => setMobileOpen(true)}
        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
      >
        <Menu size={20} />
      </button>
      <div className="flex items-center gap-2">
        {Icon && <Icon size={18} className="text-orange-500" />}
        <span className="font-semibold text-gray-800 capitalize">{current?.label || "Dashboard"}</span>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <div className="w-7 h-7 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
          🕉️
        </div>
      </div>
    </header>
  );
}
